import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import Button from 'components/atoms/Button';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import { FormDatePicker } from 'components/molecules/FormDatePicker';
import { FormInput } from 'components/molecules/FormInput';
import { FormSelect } from 'components/molecules/FormSelect';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { EButtonSizes, EButtonSizesPX, EButtonVariants } from 'constants/Buttons';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { prepareFilters } from 'helpers/filters';
import { useTranslations } from 'hooks/useTranslations';
import { useLang } from 'models/langContext';
import { EDictionaryTypes } from 'models/dictionary';
import { StyledSearchForm } from 'theme/styles';
import {
  ELicenceFormLabels,
  ILicenceConfigurationSearchFormValues,
  initialSearchFormValues,
} from '../helper';
import { EIconTypes } from 'constants/Icons';
import { useConfigurationLicence } from '../hooks';

interface ILicenceSearchForm {
  onSubmit: (arg: ILicenceConfigurationSearchFormValues) => void;
  initialSearchForm: ILicenceConfigurationSearchFormValues;
}

const LicenceSearchForm = ({ onSubmit, initialSearchForm }: ILicenceSearchForm) => {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = useForm<ILicenceConfigurationSearchFormValues>({
    defaultValues: initialSearchForm,
  });

  const [values, setValues] =
    useState<ILicenceConfigurationSearchFormValues>(initialSearchForm);

  const { t } = useTranslations();
  const { selectedLanguage } = useLang();
  const { loadDictionaries, legalType, weaponLicenceTemplateStatus } =
    useConfigurationLicence();
  const { state: routerState }: any = useLocation();
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      await loadDictionaries([
        EDictionaryTypes.legalType,
        EDictionaryTypes.weaponLicenceTemplateStatus,
      ]);
    })();
  }, [selectedLanguage]);

  const setLegalEntity = () => {
    const formValues = {
      ...initialSearchForm,
      ...routerState?.formValues,
    };

    setValues(formValues);
    reset(formValues);
  };

  useEffect(() => {
    (async () => {
      if (routerState?.formValues) {
        const formValues = {
          ...initialSearchForm,
          ...routerState.formValues,
          legalityUniqueId: routerState?.legalityUniqueId,
          keeperUniqueId: routerState?.options?.keeperOptions[0]?.value || '',
          ownerUniqueId: routerState?.options?.ownerOptions[0]?.value || '',
        };

        setValues(formValues);
        reset(formValues);
      }

      if (routerState?.legalEntity) {
        setLegalEntity();
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      await handleSubmit(onSubmit)();
    })();
  }, []);

  useEffect(() => {
    const subscription = watch((value) => {
      setValues(value as ILicenceConfigurationSearchFormValues);
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  const handleResetField = (name?: string, refreshResults: boolean = true) => {
    if (name) {
      reset({ ...getValues(), [name]: '' });
    } else {
      reset(initialSearchFormValues);
    }

    if (refreshResults) {
      onSubmit(getValues());
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid
        container
        spacing={2}
        sx={{ justifyContent: 'flex-start', py: 2, mt: '4px', flexWrap: 'nowrap', gap: 0 }}
      >
        <FormInput
          charsLimit={120}
          maxLength
          InputProps={{
            style: {
              height: '40px',
              margin: 0,
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root fieldset': {
              borderEndEndRadius: 0,
              borderStartEndRadius: 0,
            },
          }}
          withValidation={false}
          name="codeOrName"
          withLabel={false}
          placeholder={t(ELicenceFormLabels.codeOrName)}
          control={control}
          setValue={setValue}
          errors={errors}
        />
        <Button
          variant={EButtonVariants.contained}
          size={EButtonSizes.medium}
          label={t('global.search.button')}
          type="submit"
          sx={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
        />
        <Button
          variant={EButtonVariants.outlined}
          sizePX={EButtonSizesPX.px40}
          icon={EIconTypes.filters2}
          type="submit"
          sx={{ ml: '20px' }}
          onClick={() => setShowAdvanced(!showAdvanced)}
        />
      </Grid>

      {showAdvanced && (
        <SearchWrapper>
          <StyledSearchForm container style={{ justifyContent: 'space-between' }} spacing={2}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormSelect
                options={mapDictionaryToOptionProp(weaponLicenceTemplateStatus)}
                name="statusUniqueId"
                label={t(ELicenceFormLabels.statusUniqueId)}
                setValue={setValue}
                control={control}
                errors={errors}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <FormSelect
                options={mapDictionaryToOptionProp(legalType)}
                name="legalTypes"
                label={t(ELicenceFormLabels.legalTypes)}
                control={control}
                errors={errors}
                setValue={setValue}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <FormDatePicker
                control={control}
                name="createDateFrom"
                label={t(ELicenceFormLabels.createDateFrom)}
                errors={errors}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
              <FormDatePicker
                control={control}
                name="createDateTo"
                label={t(ELicenceFormLabels.createDateTo)}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Button
                variant={EButtonVariants.contained}
                size={EButtonSizes.small}
                label={t('global.search.button')}
                type="submit"
                fullWidth
                alignWithInputSX={{ xs: 10, sm: 5, md: 5, lg: 2, columns: 10 }}
              />
            </Grid>
          </StyledSearchForm>
          <SelectedFilters
            name="licence"
            values={values}
            excludes={['preset', 'perPage', 'currentPage', 'removed']}
            labels={ELicenceFormLabels}
            handleDelete={handleResetField}
            showSaveFilters={false}
            saveRouterStateAfterClearAll={false}
            dictionaries={{
              legalTypes: prepareFilters(legalType, 'uniqueId', 'name'),
              statusUniqueId: prepareFilters(weaponLicenceTemplateStatus, 'uniqueId', 'name'),
            }}
          />
        </SearchWrapper>
      )}
    </form>
  );
};

export { LicenceSearchForm };
